import { makeVar } from '@apollo/client';
import { TrackedResource } from '@app-types/blackroom-app';
import { ResourceType } from '@app-types/blackroom-api';

export interface UploadBatch {
  files: TrackedResource[];
  topLevelResources: TrackedResource[];
  isCreateResourcesDone: boolean;
  parentId?: string; // Store the parentId where files are being uploaded
}

export const uploadingFilesVar = makeVar<Record<string, UploadBatch>>({});

export interface FailedUpload {
  fileName: string;
  error: string;
  attempts: number;
}

export const failedUploadsVar = makeVar<FailedUpload[]>([]);

/**
 * Store for AbortControllers to cancel in-flight requests
 */
const uploadAbortControllers = new Map<string, AbortController>();

/**
 * Register an AbortController for an upload
 */
export function registerUploadAbortController(
  uploadId: string,
  controller: AbortController,
) {
  uploadAbortControllers.set(uploadId, controller);
}

/**
 * Get all descendant uploadIds for a given folder
 * This finds descendants either by:
 * 1. parentUploadId chain (for drag-and-drop uploads where folder stubs exist)
 * 2. uploadPath prefix matching (for input file selection where files have no parentUploadId)
 */
function getDescendantUploadIds(
  files: TrackedResource[],
  folderUploadId: string,
  folderUploadPath: string,
): Set<string> {
  const descendants = new Set<string>();

  // Method 1: Find by parentUploadId chain (drag-and-drop case)
  const findByParentId = (parentId: string) => {
    const directChildren = files.filter((f) => f.parentUploadId === parentId);
    directChildren.forEach((child) => {
      descendants.add(child.uploadId);
      findByParentId(child.uploadId);
    });
  };
  findByParentId(folderUploadId);

  // Method 2: Find by uploadPath prefix (input file selection case)
  // Files inside "FolderName" have uploadPath like "FolderName/file.txt"
  // Use path segment matching to avoid false positives (e.g., "test" matching "test-folder")
  const folderSegments = folderUploadPath.split('/');
  files.forEach((file) => {
    if (file.uploadId === folderUploadId) return;

    const fileSegments = file.uploadPath.split('/');
    // File must have more segments than folder and start with same segments
    if (fileSegments.length <= folderSegments.length) return;

    const isDescendant = folderSegments.every(
      (segment, index) => fileSegments[index] === segment,
    );
    if (isDescendant) {
      descendants.add(file.uploadId);
    }
  });

  return descendants;
}

/**
 * Cancel a specific upload by its uploadId
 * This handles both individual files and folders (which contain multiple files)
 */
export function cancelUpload(uploadId: string) {
  const currentUploads = uploadingFilesVar();
  const updatedUploads = { ...currentUploads };

  Object.keys(updatedUploads).forEach((batchId) => {
    const batch = updatedUploads[batchId];

    // Check if this is a folder (either in topLevelResources or files)
    const topLevelResource = batch.topLevelResources.find(
      (r) => r.uploadId === uploadId,
    );
    const isFolder = topLevelResource?.type === ResourceType.Folder;

    if (isFolder && topLevelResource) {
      // This is a folder - cancel all files that are descendants of this folder
      const descendantIds = getDescendantUploadIds(
        batch.files,
        uploadId,
        topLevelResource.uploadPath,
      );

      // Cancel all descendant uploads
      descendantIds.forEach((descendantId) => {
        const controller = uploadAbortControllers.get(descendantId);
        if (controller) {
          controller.abort();
          uploadAbortControllers.delete(descendantId);
        }
      });

      // Also clean up the folder's own AbortController if it exists
      const folderController = uploadAbortControllers.get(uploadId);
      if (folderController) {
        folderController.abort();
        uploadAbortControllers.delete(uploadId);
      }

      // Remove folder and its descendants from files, and folder from topLevelResources
      updatedUploads[batchId] = {
        ...batch,
        files: batch.files.filter(
          (f) => f.uploadId !== uploadId && !descendantIds.has(f.uploadId),
        ),
        topLevelResources: batch.topLevelResources.filter(
          (f) => f.uploadId !== uploadId,
        ),
      };
    } else {
      // This is an individual file - cancel just this one
      const controller = uploadAbortControllers.get(uploadId);
      if (controller) {
        controller.abort();
        uploadAbortControllers.delete(uploadId);
      }

      updatedUploads[batchId] = {
        ...batch,
        files: batch.files.filter((f) => f.uploadId !== uploadId),
        topLevelResources: batch.topLevelResources.filter(
          (f) => f.uploadId !== uploadId,
        ),
      };
    }

    // Remove empty batches (no more top-level resources to show)
    if (updatedUploads[batchId].topLevelResources.length === 0) {
      delete updatedUploads[batchId];
    }
  });

  uploadingFilesVar(updatedUploads);
}

/**
 * Cancel all pending and in-progress uploads
 */
export function cancelAllUploads() {
  // Abort all in-flight requests
  uploadAbortControllers.forEach((controller) => controller.abort());
  uploadAbortControllers.clear();

  // Clear tracking
  uploadingFilesVar({});
}

/**
 * Clean up an AbortController after upload completes
 */
export function cleanupUploadAbortController(uploadId: string) {
  uploadAbortControllers.delete(uploadId);
}

/**
 * Clean up all orphaned AbortControllers (for component unmount scenarios)
 * This prevents memory leaks when users navigate away mid-upload
 */
export function cleanupAllAbortControllers() {
  uploadAbortControllers.clear();
}

/**
 * Flattens tracked resources from upload batches
 * Filters out resources without an uploadStatus (shouldn't happen with proper typing)
 */
export function flattenBatchResources(
  uploadingFiles: Record<string, UploadBatch>,
  field: 'files' | 'topLevelResources',
): TrackedResource[] {
  return Object.values(uploadingFiles)
    .flatMap((batch) => batch[field])
    .filter((resource) => resource.uploadStatus !== undefined);
}
